/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* vastpersoneeledit.ts
* 12-5-2020 - Jelmer Jellema - Spin in het Web B.V.
*/

import {Component, OnInit} from '@angular/core';
import {hash} from '../../../app/types';
import {Datarecord} from '../../../services/api/api.service';
import {Editmodelscherm} from '../../abstracts/editmodelscherm';
import {Personeel} from '../../../models/personeel';
import {PersoneelFunctie} from '../../../models/personeel_functie';
import {Basemodel} from '../../../models/basemodel';
import {Functie} from '../../../models/functie';
import {Leverancier} from '../../../models/leverancier';
import {FunctieExact} from '../../../models/functie_exact';
import {PersoneelLabel} from '../../../models/personeel_label';
import {sortBy} from 'lodash-es';

@Component({
  selector: 'vastpersoneel-edit',
  templateUrl: './vastpersoneeledit.html',
  styleUrls: ['./vastpersoneeledit.scss']
})
export class Vastpersoneeledit extends Editmodelscherm<typeof Personeel> implements OnInit {
  _starttitle = 'Personeel';
  icon = 'fas fa-people-carry';
  model = Personeel;

  defaultRecord = {
    actief: true,
    inlog_actief: true,
    werkmaatschappij_id: this.api.userdata.werkmaatschappij //want hier werken we in
  };

  lookupQueries = {
    functie: {
      model: Functie, //be: altijd alleen uit ingelogde wm
      submodels: [FunctieExact],
      sortBy: 'naam',
      aclModel: Personeel
    },
    leverancier: {
      model: Leverancier, //be: altijd alleen uit ingelogde wm
      aclModel: Personeel
    }
  };

  /* we vragen de functies op als lookup (hierboven) gekoppeld aan Exact. Dat geeft dus alleen functies die in deze WM zitten en een koppeling met exact hebben.
  We koppelen hieronder ook rechtstreeks op PersoneelFunctie, zodat we naam en kleur hebben van functies die niet langer beschikbaar zijn
   */
  defaultSubmodels = [PersoneelFunctie, Functie, PersoneelLabel];
  protected defaultSuboptions: [typeof Basemodel, hash<any>][] = [
    [PersoneelFunctie, {
      attributes: ['id', 'functie_id', 'hoofdfunctie', 'einddatum_certificering'],
      required: false
    }],
    [Functie, {attributes: ['id', 'naam', 'kleur']}]
  ];


  ux = {
    nieuwefunctie: <Functie> null, //gekozen functie voor toevoegen
    nieuwlabelkeuze: <string> null, //nieuw label uit keuzelijst
    nieuwlabelinvoer: <string> null //nieuw label uit invoer
  };

  labels: string[] = [];

  //constructor ivm injection
  constructor() {
    super('debug');
  }

  async ngOnInit() {
    await super.ngOnInit();
    if (!this.readonly) {
      //mogelijke labels ophalen
      this.labels = await Personeel.alleLabels();
    }
  }

  /**
   * Verwerk ook even de functies
   */
  protected async afterHaaldata(): Promise<void> {
    await super.afterHaaldata();
    this.record.personeelvast_functie = this.record.personeelvast_functie || [];
    for (let f of this.record.personeelvast_functie) {
      f._tags.verwijderd = false; //is deze verwijderd?
    }

    //we sorteren nu eenmaal goed, zodat de hoofdfunctie bovenaan staat
    //daarna niet meer, anders verspringt alles bij wijzigen hoofdfunctie
    this.record.personeelvast_functie.sort((f1: PersoneelFunctie, f2: PersoneelFunctie) => {
      if (f1.hoofdfunctie) {
        return -1;
      }
      if (f2.hoofdfunctie) {
        return 1;
      } else {
        return f1.functie.naam.localeCompare(f2.functie.naam);
      }
    });
  }

  /**
   * Bij wijziging van actie. We zetten dan ook inloggen uit
   */
  actiefGewijzigd() {
    if ((!this.record.actief) && (this.record.orig('actief'))) {
      this.record.inlog_actief = false;
    }
  }

  /**
   * irritante ommapping van personeelsgroep naar leverancierstype
   */
  groepNaarLeverancierstype(): string {
    return {
      'Extern': 'Inhuur',
      'ZZP': 'ZZP Pool' //todo: moet dit zo of moet er een aparte leveranciersgroep komen
    }[this.record.groep || '-'] || this.record.groep;
  }

  //en bij het opslaan ook
  protected savedata(): Datarecord {
    //geen supercall, die geeft gewoon this.record terug
    let clone = this.record.fullClone();
    clone.personeelvast_functie = clone.personeelvast_functie.filter(pf => !pf._tags.verwijderd);
    //en de functies ertussenuit
    for (let pv of clone.personeelvast_functie) {
      delete pv.functie; //wat een gerommel
    }
    let data = clone.savedata();

    if (this.record._tags.wachtwoord?.length) {
      data.wachtwoord = this.record._tags.wachtwoord;
    }
    return data;

  }


  /**
   * Dynamische titel
   */
  get title(): string {
    if (this.isNieuw) {
      return 'Nieuw vast personeel';
    }
    //even taggen als het er niet is
    return this.orignaam;
  }

  //recordbeschrijving
  get recordBeschrijving(): string {
    return this.isNieuw ? 'dit nieuwe personeelslid' : this.orignaam;
  }

  /**
   * Helpertje
   */
  get orignaam(): string {
    //al getagd?
    if (!this.record._tags.orignaam) {
      this.record._tags.orignaam = this.record.naam;
    }
    return this.record._tags.orignaam;
  }

  /**
   * typecast
   */
  get functieLookups(): Functie[] {
    return <Functie[]> this.lookups.functie;
  }

  //functies
  maakHoofdfunctie(rij) {
    //mag deze functie wel?
    if (!this.functieExactVoor(rij.functie_id).length) {
      this.log.warn(`Functie niet goed gekoppeld aan exact.`);
      return; //gewoon niet doen
    }
    //de andere dus niet
    for (let f of this.record.personeelvast_functie) {
      if (f.functie_id === rij.functie_id) {
        if (!f.hoofdfunctie) {
          this.setDirty();
          f.hoofdfunctie = true;
        }
      } else {
        f.hoofdfunctie = false;
      }
    }
  }

  /**
   * Verwijder de opgegeven functie, of zet een eerder verwijderde weer terug. Verwijderen gebeurt
   * pas echt bij het opslaan, zie savedata()
   * @param pf
   */
  verwijderFunctie(pf: PersoneelFunctie) {
    pf._tags.verwijderd = !pf._tags.verwijderd;
    if (pf._tags.verwijderd && pf.hoofdfunctie) {
      pf.hoofdfunctie = false; //voor als hij terugkomt
      this.checkHoofdfunctie(); //kies een nieuwe
    }
    this.setDirty();
    //Angular schakelt het datumveld van deze rij pas hierna in of uit, en zet het formulier daarbij
    //terug op pristine als dat veld de enige wijziging was. Daarom markeren we opnieuw als dat gebeurd is.
    setTimeout(() => this.setDirty());
  }

  /**
   * Geeft true als de certificering voor deze functie vandaag verlopen is. Een rij die op verwijderen
   * staat laten we met rust: die telt straks toch niet meer mee.
   * @param pf
   */
  certificeringVerlopen(pf: PersoneelFunctie): boolean {
    return (!pf._tags.verwijderd) && (!pf.geldigOp());
  }

  /**
   * Levert de waarschuwingstekst als de functie niet goed in Exact staat, en anders null. Er moet
   * binnen deze werkmaatschappij óf één algemeen artikel zijn, óf de drie artikelen dag/nacht/wknd
   * @param pf
   */
  exactWaarschuwing(pf: PersoneelFunctie): string {
    const fes = this.functieExactVoor(pf.functie_id);
    const functie = this.vindFunctie(pf); //bij nieuwe is de koppeling er niet
    if (!fes.length) {
      return `Deze functie ontbreekt in Exact. Dit geeft problemem met inkoop en verkoop. Voeg deze functie als artikel toe in Exact met precies dezelfde naam. Je kan ook drie artikelen maken, namelijk "${functie?.naam} dag", "${functie?.naam} nacht" en "${functie?.naam} wknd".`;
    }
    //dan moet er 1 record zijn, of 3 records met dag/nacht/wknd, in deze wm
    if (
      (fes.length === 1 && fes[0].tijdsperiode === 'alg')
      ||
      (fes.length === 3 && ['dag', 'nacht', 'wknd'].every(tp => fes.some(fe => fe.tijdsperiode === tp)))
    ) {
      return null;
    }
    return `Deze functie is niet compleet in Exact. Dit geeft problemen met inkoop en verkoop. De functie moet in Exact als artikel bestaan met precies de juiste naam: óf precies "${functie?.naam}", of de drie artikelen "${functie?.naam} dag", "${functie?.naam} nacht" en "${functie?.naam} wknd".`;
  }


  /**
   * vind een functie bij een personeelsfunctie
   * de lookup heeft hem niet als er een probleem is in deze wm met deze functie en exact
   * het eigen functie-subrecord heeft hem niet bij toevoegen
   * @param pv
   */
  vindFunctie(pv: PersoneelFunctie): Functie {
    return (<Functie> (this.lookupHashes && this.lookupHashes.functie && this.lookupHashes.functie[pv.functie_id])) || pv.functie;
  }

  /**
   * Geef de functieExact records voor de opgegeven functieid, gegeven de wm
   * @param id
   */
  functieExactVoor(id: number): FunctieExact[] {
    //de functie is wel nodig, maar filteren op wm niet meer: het is altijd waar we in werken
    //
    const f = <Functie> (this.lookupHashes.functie && this.lookupHashes.functie[id]);
    return (f?.functie_exact || []).filter(fe => fe.werkmaatschappij_id === this.record.werkmaatschappij_id);
  }

  /**
   * ux: check of een bepaalde functie toegevoegd kan worden. Voor vullen select
   * @param functie
   */
  kanFunctieToevoegen(functie: Functie): boolean {
    if (!(this.record.personeelvast_functie)) {
      return false; //nog niet geinit, straks weer proberen
    }
    if (this.record.personeelvast_functie.some(f => f.functie_id === functie.id && (!f._tags.verwijderd))) {
      return false; //kan niet want is er al
    }
    //juiste wm?
    //overbodig, want functie_exact is door be al gefilterd op de wm
    /*    if (!(this.record.werkmaatschappij_id && functie.functie_exact.some(fe => fe.werkmaatschappij_id === this.record.werkmaatschappij_id))) {
          return false;
        }*/
    //in principe kan het dan
    return true;
  }

  /**
   * Voeg de nieuw geselecteerde functie toe
   */
  nieuwefunctie() {
    if (this.ux.nieuwefunctie) {
      //was hij verwijderd?
      let verwijderd = this.record.personeelvast_functie.find(f => f.functie_id === this.ux.nieuwefunctie.id && f._tags.verwijderd);
      if (verwijderd) {
        verwijderd._tags.verwijderd = false; //nu niet meer
      } else {
        //nieuwe
        this.record.personeelvast_functie.push(PersoneelFunctie.create({
          functie_id: this.ux.nieuwefunctie.id,
          hoofdfunctie: false
        }, this.record));
      }
      this.ux.nieuwefunctie = null;
      this.checkHoofdfunctie();
      this.setDirty();
    }
  }

  /**
   * Helper, zorg dat er altijd een hoofdfunctie is
   */
  checkHoofdfunctie() {
    if (!(this.record && this.record.personeelvast_functie)) {
      return;
    }
    if (this.record.personeelvast_functie.some(f => f.hoofdfunctie && (!f._tags.verwijderd))) {
      return; //klaar
    }
    //ok, eerste de beste
    let nieuwehoofd = this.record.personeelvast_functie.find(f => !f._tags.verwijderd);
    if (nieuwehoofd) {
      nieuwehoofd.hoofdfunctie = true;
      this.setDirty(); //er is iets gewijzgd
    }
  }

  /**
   * Voeg een label toe, dit is een vrije string
   * @param label
   */
  nieuwLabel(label: string) {
    if (!this.record.heeftLabel(label)) {
      this.record.personeel_label.push(PersoneelLabel.create({label: label}, this.record));
      this.record.personeel_label = sortBy(this.record.personeel_label, 'label');
      this.setDirty()
    }
  }

  /**
   * Verwijder het opgegeven label
   * @param label
   */
  verwijderLabel(label: PersoneelLabel) {
    this.record.personeel_label = this.record.personeel_label.filter(l => l !== label);
    this.setDirty();
  }


  protected readonly Personeel = Personeel;
}
